import type { ComponentChildren } from "preact";
import type {
  DailyLog, EnergyProfile, HealthMetricMap, IsoDate, LogMap, Measurement
} from "../domain/types.js";
import type { MetricCardData } from "./MetricCard.js";
import type { MeasurementView, TrendCell } from "../domain/measurement-view.js";
import {
  chartSeries, dayMeasurement, revealedRows, trendCells
} from "../domain/measurement-view.js";
import {
  PLAN_START, PLAN_END, RACE_DATE_LABEL, TARGET_END_WEIGHT, countdownInfo
} from "../domain/plan.js";
import { weekPlan } from "../domain/week-plan.js";
import { workoutFor } from "../domain/workout.js";
import { formatDate } from "../domain/dates.js";
import { SEASON_1 } from "../../shared/plan.js";
import { seasonBaseline } from "../domain/measurements.js";
import { fmt, kcal } from "../domain/numbers.js";
import { dayCompletion } from "../domain/scoring.js";
import { energyGuideFor } from "../domain/energy.js";
import { weeklyCompliance } from "../domain/stats.js";
import { battleReport } from "../domain/report.js";
import { XP_PER_LEVEL, npcDialogue, weightBossProgress, playerStats } from "../domain/progression.js";
import { badges } from "../domain/badges.js";
import { settlePredictions } from "../domain/settlement.js";
import { cheerLeaderboard, cheerParticipants } from "../domain/cheers.js";
import { cheeredBy, cheeredOn, myPrediction } from "../domain/supporter.js";
import { BadgeWall } from "./BadgeWall.js";
import { DayEditor } from "./DayEditor.js";
import { missingStepsDays } from "../domain/backfill.js";
import { Calendar } from "./Calendar.js";
import { Arrow } from "./Arrow.js";
import { MetricCards } from "./MetricCard.js";
import { TabIcon } from "./TabIcon.js";
import { LineChart, ComplianceChart } from "./charts.js";
import { ShareMetrics, ShareCompliance, ShareAdvice, SyncStatus, EnergyPreview } from "./Share.js";
import type { SyncStatusData } from "./Share.js";
import type { BadgeStatus } from "../storage/app-badge.js";
import type { CheerRow, PredictionRow, RacePredictionRow, RaceResultRow } from "../../shared/types.js";
import {
  canBetOnRace, canEditRaceResult, finishTimeParts, formatFinishTime, joinFinishTime,
  RACE_CHECKPOINTS, RACE_LIMIT_HOURS, RACE_START_TIME
} from "../../shared/race-prediction.js";
import { Wheel, wheelValue } from "./Wheel.js";
import type { RaceSettlement } from "../domain/race-settlement.js";
import { finishOutcome, settleRace } from "../domain/race-settlement.js";
import type { BetStatus } from "../domain/bet-outcome.js";
import { betResult, betTone, doublePredictors, raceBetResults } from "../domain/bet-outcome.js";
import type { RevealState } from "./Awards.js";
import { DoubleRibbon, MyBetResult, QuestionAwards, formatFinish, formatKg } from "./Awards.js";
import { seasonReport } from "../domain/season-report.js";
import type { InstallHint as InstallHintData } from "../domain/install-hint.js";
import type { Role } from "../../shared/roles.js";
import { can } from "../../shared/roles.js";
import { NICKNAME_MAX_WIDTH } from "../../shared/nickname.js";
import type { Notice } from "../domain/notice.js";

/**
 * 整個畫面的唯一 root。
 *
 * 先前 index.html 與 main.js 之間的 interface 是 40 多個 element id，
 * 加上一份手動維護的重繪清單（refreshDailyViews 列出五件要重畫的東西）。
 * 現在只有一個 root，重繪範圍交給 diff。
 *
 * props 刻意只有五個：資料、今天、身體資料、UI 狀態、動作。
 * 各分頁需要什麼自己用 domain 的純函式算——那些函式本來就不讀全域狀態，
 * 所以把算好的值一個個往下傳只會讓 interface 變寬而已。
 */

export type TabName =
  | "today" | "calendar" | "stats" | "share" | "settings" | "cheer" | "prediction"
  /** 賽季成績單。不在底部選單裡，由入口按鈕進來。 */
  | "report";

export interface AppData {
  /**
   * 量測資料的視圖（`src/domain/measurement-view.ts`）。
   *
   * 先前這裡是三個並排的欄位：`measurements` ＋ `redacted` ＋ `directions`。
   * 那個形狀要求每一個呼叫端自己記得「遮蔽時不可以拿這些數字算任何有單位的
   * 東西」，而違反它不會報錯——只會印出很像真的假數字。現在那件事寫在型別
   * 裡：遮蔽分支上根本沒有 `weight_kg` 這個欄位。見 `docs/adr/0004`。
   */
  measurements: MeasurementView;
  /**
   * 第一季的體重，一律是真實公斤。賽季起點與舊競猜的結算都要它。伺服器還沒
   * 揭曉時是空陣列，所以不可能拿無單位的值去算。
   */
  season1Measurements: Measurement[];
  logs: LogMap;
  healthMetrics: HealthMetricMap;
  /** 誰在哪一天戳過。一人一天一次，所以 (日期, 暱稱) 不會重複。 */
  cheers: CheerRow[];
  /** 誰猜生日目標日達不達得到。一人一筆。 */
  predictions: PredictionRow[];
  /** 誰猜台北馬賽季能不能完賽、體重能不能達標。一人一筆。 */
  racePredictions: RacePredictionRow[];
  /** 官方完賽成績。還沒補登是 null。 */
  raceResult: RaceResultRow | null;
  /** 第一季的紀錄，賽季成績單用。 */
  season1Logs: LogMap;
  season1HealthMetrics: HealthMetricMap;
}

export interface AppUiState {
  activeTab: TabName;
  loginVisible: boolean;
  loginError: string;
  toast: string;
  toastVisible: boolean;
  refreshing: boolean;
  /** 這次重抓失敗的資料來源。畫面用它分辨「讀不到」與「真的沒有」。 */
  failedLoads: string[];
  calendarMonth: string;
  selectedDate: IsoDate;
  npcOffset: number;
  mascotBouncing: boolean;
  /**
   * 剛戳完，回條上的肉羹要彈一下。
   *
   * **與 `mascotBouncing` 分開**：那支給今日任務頁的 NPC 肉羹用，共用的話
   * 戳一下會讓兩隻同時彈。
   */
  cheerBouncing: boolean;
  syncStatus?: SyncStatusData;
  syncError?: string;
  syncEndpoint: string;
  buildInfo: string;
  /** 目前展開的說明 tooltip，用卡片標籤識別。 */
  openHelp: string | null;
  /**
   * 目前展開的補登面板，`null` 是全部收起來（預設）。
   *
   * `date` 一併記是因為 `DayEditor` 會同時渲染兩次——只記 `kind` 的話今日與
   * 選取日會一起展開。
   */
  openBackfill: { kind: "steps" | "body"; date: IsoDate } | null;
  /** 任務角標的支援狀況與偏好，預設關。 */
  badge: BadgeStatus;
  /** 監督者自己的暱稱，存在他的裝置上。本人用不到。 */
  nickname: string;
  /** 暱稱不合規時的說明，合規時是空字串。 */
  nicknameError: string;
  /** 下注失敗的說明（例如已鎖定），成功時是空字串。 */
  predictionError: string;
  /** 完賽成績存不起來的說明，成功時是空字串。 */
  raceResultError: string;
  /**
   * 目前展開的成就徽章 id，沒有展開是 null。
   *
   * 與 `badge`（任務角標）無關——那是 App 圖示上的數字，這是統計頁的徽章牆。
   */
  openAchievement: string | null;
  /** 要顯示的公告，沒有就是 null。已經套用過「關掉過沒」與「是不是已安裝」。 */
  notice: Notice | null;
  /** 加入主畫面的提示。已經套用過角色、環境與「關掉過沒」，沒有就是 null。 */
  installHint: InstallHintData | null;
  /** 這台裝置看過哪些開獎的彩帶。 */
  seenReveals: string[];
  /** LINE 裡換到外部瀏覽器用的連結。 */
  externalBrowserHref: string;
  /** 線上有比手上這份新的建置。 */
  updateAvailable: boolean;
  /** 手動補登的草稿。 */
  backfill: BackfillDraft;
}

/**
 * 一份補登草稿共通的部分。
 *
 * `date` 是「正在編輯哪一天」，不是「顯示哪一天」——`DayEditor` 同時渲染
 * 今日與選取日兩份，沒有它的話在一格打字會兩格一起變。
 *
 * 數值一律存**字串**：輸入到一半的 `"8"` 不該被當成 8 步，清空的 `""` 也不該
 * 變成 0。轉數字只在送出那一刻做一次。
 */
interface DraftBase {
  date: IsoDate | null;
  status: "idle" | "saving" | "error";
  error: string;
}

/*
 * 沒有 "saved" 狀態：存成功之後面板整個收起來，那一行字沒有地方待。
 * 成功走 toast，見 `main.js` 的 onSaveSteps。
 */

export interface StepsDraft extends DraftBase {
  value: string;
}

export interface BodyDraft extends DraftBase {
  weight: string;
  fat: string;
  /**
   * 淨體重與 BMI 的空字串代表**用自動算的值**，不是「清成 null」——那兩格
   * 的自動值是用 placeholder 呈現的。體重與體脂的空字串才是「清掉這一格」。
   */
  lean: string;
  bmi: string;
}

/**
 * 手動補登的草稿，步數與身體數值各自一份。
 *
 * 合成一份共用 `date` 的話，在同一天打了體重再去存步數，存完的重置會把還
 * 沒送出的體重清掉——而使用者只會看到自己打的字消失。
 */
export interface BackfillDraft {
  steps: StepsDraft;
  body: BodyDraft;
}

/** 送給 `PUT /api/measurements` 的四個值。`null` 是「清掉這一格」。 */
export interface BodyPayload {
  weight_kg: number | null;
  body_fat_percent: number | null;
  lean_body_mass_kg: number | null;
  bmi: number | null;
}

export interface AppActions {
  onTabChange(tab: TabName): void;
  onLogin(password: string): void;
  onLogout(): void;
  onRefresh(): void;
  onToggleTask(date: IsoDate, field: keyof DailyLog, next: boolean): void;
  onNote(date: IsoDate, text: string): void;
  onSelectDate(date: IsoDate): void;
  onCalendarShift(delta: number): void;
  onCalendarToday(): void;
  onOpenCalendar(): void;
  onMascotClick(): void;
  onMascotAnimationEnd(): void;
  onCheerAnimationEnd(): void;
  onSaveProfile(profile: EnergyProfile): void;
  onRecheckSync(): void;
  onDownloadCsv(): void;
  onCopySummary(): void;
  onPrint(): void;
  onToggleHelp(label: string): void;
  onToggleBadge(next: boolean): void;
  onToggleAchievement(id: string): void;
  onSaveNickname(nickname: string): void;
  /** 還沒有暱稱就想戳：跳到戳戳樂分頁並把游標放進輸入框。 */
  onNeedNickname(): void;
  onCheer(date: IsoDate): void;
  /** 跳到某一天的月曆。漏填提示點下去用的。 */
  onGoToDate(date: IsoDate): void;
  onToggleBackfill(kind: "steps" | "body", date: IsoDate): void;
  onBackfillSteps(date: IsoDate, value: string): void;
  onSaveSteps(date: IsoDate): void;
  onBackfillBody(date: IsoDate, field: "weight" | "fat" | "lean" | "bmi", value: string): void;
  onSaveBody(date: IsoDate, payload: BodyPayload): void;
  onBet(bet: {
    will_finish: boolean;
    will_reach_weight: boolean;
    weight_kg: number | null;
    /** 還沒解析的字串。空字串是沒填，格式不對由 main.js 講。 */
    finish_time: string;
  }): void;
  onSaveRaceResult(result: {
    outcome: "finished" | "not_finished" | "cancelled";
    finish_time: string;
  }): void;
  onDismissNotice(id: string): void;
  onDismissInstallHint(): void;
  onRevealSeen(id: string): void;
  onReload(): void;
}

export interface AppProps {
  data: AppData;
  today: IsoDate;
  /** 夾在計畫期間內的今天，所有「操作對象」用這個。 */
  planDate: IsoDate;
  profile: EnergyProfile;
  /**
   * 本人還是監督者。是輸入而不是算好的值，所以與 today、profile 同級。
   * 能不能編輯一律問 shared/roles.ts 的 can()，不要在這裡自己判斷角色。
   */
  role: Role;
  ui: AppUiState;
  actions: AppActions;
}

import type { IconName } from "./TabIcon.js";

type TabDef = [tab: TabName, icon: IconName, label: string];

/**
 * 分頁依角色不同，但**兩邊都是五格**——底部選單的寬度不因角色而變。
 *
 * 監督者沒有分享與設定：那兩頁整頁都只跟本人有關（戰報、代謝設定、同步狀態、
 * 匯出、版本號）。空出來的位置給「身分」，那是他填暱稱與登出的地方。
 */
const TABS_BY_ROLE: Record<Role, TabDef[]> = {
  owner: [
    ["today", "today", "今日任務"],
    ["calendar", "calendar", "月曆"],
    ["stats", "stats", "統計"],
    ["share", "share", "分享"],
    ["settings", "settings", "設定"]
  ],
  viewer: [
    ["today", "today", "今日任務"],
    ["calendar", "calendar", "月曆"],
    ["stats", "stats", "統計"],
    ["prediction", "prediction", "競猜"],
    ["cheer", "cheer", "戳戳樂"]
  ]
};

export function tabsFor(role: Role): TabDef[] {
  return TABS_BY_ROLE[role] || TABS_BY_ROLE.viewer;
}

function LoginOverlay({ visible, error, onLogin }: {
  visible: boolean;
  error: string;
  onLogin: (password: string) => void;
}) {
  return (
    <div id="loginOverlay" class={`overlay ${visible ? "" : "hidden"}`}>
      <form
        id="loginForm"
        class="login-card brutal-card"
        onSubmit={event => {
          event.preventDefault();
          const input = (event.currentTarget as HTMLFormElement)
            .querySelector<HTMLInputElement>("#passwordInput");
          if (input) { onLogin(input.value); input.value = ""; }
        }}
      >
        <img class="login-mascot" src="/icon-display.png" alt="肉羹貓貓舉重" />
        <p class="eyebrow">FIGHT HEALTH</p>
        <h1>登入健康冒險</h1>
        <p>輸入 Cloudflare 儀表板密碼，繼續今天的任務。</p>
        <input id="passwordInput" type="password" autocomplete="current-password" placeholder="密碼" required />
        <button type="submit">開始挑戰 →</button>
        <div id="loginError" class="error-text">{error}</div>
      </form>
    </div>
  );
}

/** 側欄：品牌列、分頁、玩家狀態。等級與 EXP 由 props 推導，不再手寫 DOM。 */
function Sidebar({ data, planDate, role, ui, actions }: AppProps) {
  // rows 只用來判斷「那天有沒有量」（有就 +EXP），兩支都答得出來，
  // 所以監督者的等級與連勝在遮蔽下照樣是對的。見 progression.ts 的 MeasuredDay。
  const stats = playerStats({ ...data, measurements: data.measurements.rows }, planDate);
  const xpPercent = stats.levelXp / XP_PER_LEVEL * 100;

  /**
   * 生日 Boss 的 HP **對監督者完全不給**，不是改成箭頭。
   *
   * `percent = (起始體重 − 目前體重) / (起始體重 − 目標體重) × 100`，而那個
   * 百分比直接印在畫面上——知道它就等於知道體重，是一條完整的反推公式，比
   * 體重徽章的階梯更直接。這是 `docs/adr/0004`「只能有一個參考點」擋的東西。
   *
   * 順帶一提，拿遮蔽後的 0–1 去算會得到 `82.9 − 0.5`，夾到 100% 之後顯示
   * 「HP：0%」，等於對監督者謊稱目標已經達成——不報錯，但完全是假的。
   * 現在型別擋著：`weightBossProgress` 收不到遮蔽分支的 rows。
   */
  const boss = data.measurements.kind === "revealed"
    ? weightBossProgress(data.measurements.rows, seasonBaseline(data.season1Measurements))
    : null;

  /**
   * **「是本人才給本人版」而不是「是監督者才換」。**
   *
   * 與 shared/roles.ts 的遮蔽函式同一條理由：角色漏傳、拼錯或未來多一種角色
   * 時，前者仍然安全。這裡的「安全」是預設落到後援會版——最壞情況是本人看到
   * 一隻後援會肉羹（怪，但無害），而不是監督者看到一格沒標記的等級，那正是
   * 這一票要修的東西。tabsFor 的 `|| TABS_BY_ROLE.viewer` 是同一個 fallback 方向。
   */
  const isOwner = role === "owner";

  return (
    <aside class="sidebar">
      <div class="brand-panel">
        <div class="brand-title"><span>FIGHT</span><strong>HEALTH</strong></div>
        <img
          class="brand-mascot"
          src={isOwner ? "/icon-display.png" : "/mascot-supporter.png"}
          alt={isOwner ? "肉羹貓貓舉重" : "後援會肉羹拿著大聲公，身後一群支持者"}
        />
        <div class="level-line">
          <b id="playerLevel">LV.{stats.level}</b>
          {/*
            監督者版把稱號拆成「連接詞＋名字」，名字上黃色。

            **不能用 `<b>`**——`.level-line b` 是那顆黃底 LV chip 的選取器，
            用 `<b>` 包名字會讓它也長成一顆 chip。
          */}
          <span id="playerTitle">
            {isOwner ? stats.title : <>盯著<em class="watched-title">{stats.title}</em></>}
          </span>
        </div>
        <div class="xp-wrap">
          <div class="xp-track"><i id="playerXpBar" style={{ width: `${xpPercent}%` }} /></div>
          {/*
            30px 的版本與側欄那張 132px 是**兩個不同的檔案**，不是同一張縮放：
            牌子版在 30px 下只剩一塊黃，去背版才看得出是一隻舉手的貓。
          */}
          <img
            id="xpMascot" class="xp-mascot"
            src={isOwner ? "/mascot.png" : "/mascot-supporter-mini.png"}
            alt="" aria-hidden="true"
            style={{ "--xp": `${xpPercent}%` } as Record<string, string>}
          />
        </div>
        <small id="playerXpText">
          EXP {stats.levelXp.toLocaleString("zh-TW")} / {XP_PER_LEVEL.toLocaleString("zh-TW")}
        </small>
      </div>

      {/* --tab-count 給手機版的格線用：格數依角色而變，寫死五欄會留一格空白 */}
      <nav
        class="tabs" aria-label="主選單"
        style={{ "--tab-count": tabsFor(role).length } as Record<string, number>}
      >
        {tabsFor(role).map(([tab, icon, label]) => (
          <button
            key={tab}
            data-tab={tab}
            class={`tab ${ui.activeTab === tab ? "active" : ""}`}
            onClick={() => actions.onTabChange(tab)}
          >
            <span><TabIcon name={icon} /></span><b>{label}</b>
          </button>
        ))}
      </nav>

      <div class="quest-card">
        <div class="quest-card-head">
          <b>PLAYER STATUS</b>
          <span id="playerStreak">🔥 {stats.streak} 連勝</span>
        </div>
        <p id="playerStatusLine">
          {stats.streak ? `連續 ${stats.streak} 天達成 A Rank 以上。` : "完成 4/5 任務即可延續連勝。"}
        </p>
        <div class="heart-bar" aria-label="體重 Boss 進度">
          <i id="bossProgressBar" style={{ width: `${boss ? boss.percent : 0}%` }} />
        </div>
        <small id="bossProgressText">{boss ? boss.label : `體重 Boss HP：${RACE_DATE_LABEL} 揭曉`}</small>
      </div>
    </aside>
  );
}

function AppHeader({ today, ui, actions }: AppProps) {
  const label = new Intl.DateTimeFormat("zh-TW", {
    timeZone: "Asia/Taipei", year: "numeric", month: "2-digit", day: "2-digit", weekday: "short"
  }).format(new Date(`${today}T12:00:00+08:00`)).replaceAll("/", " / ");

  return (
    <header class="app-header">
      <div class="header-copy">
        <p class="eyebrow">HEALTH QUEST DASHBOARD</p>
        <h1>台北馬賽季</h1>
        <div class="subtitle">完賽半馬 × 體重 {TARGET_END_WEIGHT} kg｜{RACE_DATE_LABEL} 開跑</div>
      </div>
      <div class="header-actions">
        <div class="today-card">
          <span>TODAY</span>
          <b id="todayLabel">{label}</b>
        </div>
        <button
          id="refreshBtn" class="icon-btn" aria-label="重新整理"
          disabled={ui.refreshing} onClick={actions.onRefresh}
        >↻</button>
      </div>
    </header>
  );
}

function TabPanel({ name, active, children }: {
  name: TabName;
  active: boolean;
  children: ComponentChildren;
}) {
  return <section id={`tab-${name}`} class={`tab-panel ${active ? "active" : ""}`}>{children}</section>;
}

/**
 * 那一天有誰來過。**只有本人看得到名字**——監督者看得到月曆上的愛心數，
 * 但看不到是誰，否則就等於把「誰特別常來」攤在所有人面前。
 */
/**
 * 切到戳戳樂分頁，並把游標放進暱稱欄。
 *
 * `#nicknameInput` 的知識留在這裡而不是 DayEditor——這個檔案同時擁有那個
 * 表單與戳一下的接線，DayEditor 只需要知道「這個人還沒有名字」。
 *
 * 分頁切換是狀態（走 actions），捲動與聚焦是一次性的副作用，所以留在事件
 * 處理器裡。這個 codebase 刻意沒有用任何 hook，不為了這件事開先例。
 */
function goToNicknameField(actions: AppActions) {
  actions.onNeedNickname();
  requestAnimationFrame(() => {
    const input = document.getElementById("nicknameInput") as HTMLInputElement | null;
    input?.scrollIntoView({ block: "center" });
    input?.focus({ preventScroll: true });
  });
}

/**
 * 那一天有誰來過。**只有本人看得到名字**——監督者看得到月曆上的愛心數，
 * 但看不到是誰，否則就等於把「誰特別常來」攤在所有人面前。
 *
 * 「誰戳了那天」由 `domain/supporter.ts` 答，「誰看得到」是能力問題留在這裡。
 */
function cheersOn({ data, role }: AppProps, date: IsoDate): string[] {
  if (role !== "owner") return [];
  return cheeredOn(data.cheers, date);
}

function dayEditorPropsFor(props: AppProps, date: IsoDate, heading: string) {
  const { data, today, planDate, role, ui, actions } = props;
  const log = data.logs[date] || {};
  const metric = data.healthMetrics[date] || {};
  return {
    date, today, heading, log, metric,
    backfill: ui.backfill,
    /**
     * 漏填提示只掛在今日那一份上。掛在選取日的話，翻月曆時每一天都會跳出
     * 同一句「還有 N 天沒有步數」——那不是提示，是噪音。
     */
    missingSteps: date === planDate
      ? missingStepsDays(data.healthMetrics, today, PLAN_START, PLAN_END)
      : [],
    openBackfill: ui.openBackfill,
    onToggleBackfill: (kind: "steps" | "body") => actions.onToggleBackfill(kind, date),
    onBackfillSteps: (value: string) => actions.onBackfillSteps(date, value),
    onSaveSteps: () => actions.onSaveSteps(date),
    onBackfillBody: (field: "weight" | "fat" | "lean" | "bmi", value: string) =>
      actions.onBackfillBody(date, field, value),
    onSaveBody: (payload: BodyPayload) => actions.onSaveBody(date, payload),
    // BMI 要身高，而身高只存在這台裝置的 localStorage 裡——後端沒有它，
    // 所以推導在前端做、送出的是算好的數字。
    heightCm: props.profile.heightCm,
    onGoToDate: actions.onGoToDate,
    // 角色整個傳下去，讓 DayEditor 自己問能力清單——這裡展開成 canEdit、
    // canCheer 的話，每多一種能力就要多一個 prop。
    role,
    nickname: ui.nickname,
    cheeredByMe: cheeredBy(data.cheers, date, ui.nickname),
    onCheer: () => actions.onCheer(date),
    onNeedNickname: () => goToNicknameField(actions),
    // 「那天有沒有量」「量了多少」「是升是降」三件事一起來，由 view 決定
    // 哪一個答得出來
    day: dayMeasurement(data.measurements, date),
    progress: dayCompletion(log, metric),
    onToggle: (field: keyof DailyLog, next: boolean) => actions.onToggleTask(date, field, next),
    onNote: (text: string) => actions.onNote(date, text)
  };
}

/**
 * 趨勢摘要的一格畫成卡片。
 *
 * `text` 與 `direction` 恰好一個有值（見 `TrendCell`），所以這裡不需要再問
 * 一次遮蔽與否。標籤與副標可以覆寫——那是排版，各分頁各自決定。
 */
function trendCard(cell: TrendCell, label = cell.label, note = cell.note): MetricCardData {
  return [label, cell.text ?? <Arrow direction={cell.direction} />, note];
}

function TodayTab(props: AppProps) {
  const { data, planDate, profile, role, ui, actions } = props;
  const view = data.measurements;
  const countdown = countdownInfo(planDate);
  const baseline = seasonBaseline(data.season1Measurements);
  const metric = data.healthMetrics[planDate] || {};
  const progress = dayCompletion(data.logs[planDate] || {}, metric);
  const npc = npcDialogue({
    ...data, measurements: view.rows, date: planDate, today: planDate, offset: ui.npcOffset
  });
  const cheeredToday = cheersOn(props, planDate);

  const cells = trendCells(view, planDate);

  /*
   * 7 日平均的副標把「較前週」與變化量寫在一起——那一格因此需要另一格的
   * 文字。是排版不是計算，所以在這裡組。
   */
  const weeklyNote = cells.weeklyChange.missing
    ? "資料不足"
    : `較前週 ${cells.weeklyChange.text ?? ""}`;

  /*
   * 上面這排是**趨勢**，下面的任務卡是**今天**。
   *
   * 先前這排是「最新體重／7日平均／今日步數／今日 Rank」，而往下捲一次就會
   * 看到同樣的步數（任務卡的步數區塊，還多一條進度條）與同樣的 Rank（持續
   * 推進那張卡，還多了完成數）。同一個數字在一屏裡出現兩次不會讓人更確定，
   * 只會讓真正該看的那格失去份量。
   *
   * 重複的那兩格不能從下面拿掉：任務卡與月曆的「當日編輯器」是同一個元件，
   * 那裡的步數與量測是**那一天**的唯一出處，拿掉月曆就瞎了。所以要換的是
   * 這排——換成任務卡上沒有的三個趨勢數字。
   *
   * 體脂看的是 7 日平均，跟任務卡四宮格裡「今天量到的體脂」是兩個數字，
   * 不是同一個（見 CONTEXT.md：判讀一律看平均，不看單日）。
   */
  const cards: MetricCardData[] = [
    trendCard(cells.weight, "最新體重"),
    trendCard(cells.avg7, "7日平均", view.kind === "shapeOnly" ? undefined : weeklyNote),
    trendCard(cells.avgFat7),
    trendCard(cells.prediction)
  ];
  void profile;
  const week = weekPlan(data.logs, planDate);

  return (
    <TabPanel name="today" active={ui.activeTab === "today"}>
      <article class="goal-strip brutal-card">
        <div class="goal-countdown">
          <span>距離 {RACE_DATE_LABEL} 臺北馬</span>
          <strong><b id="countdownDays">{countdown.remaining}</b><em>天</em></strong>
        </div>
        <div class="goal-progress">
          <div class="goal-copy">
            {/*
              起點與目標都是**期程常數**，不是今天的數字，所以兩種角色看到的
              一樣，也不構成遮蔽的破口——監督者本來就知道起始體重（見
              CONTEXT.md 的「揭曉日」）。

              箭頭已經說完整趟旅程，所以不再多寫一句「要減 8 kg」——那只是
              把畫面上兩個數字相減，卻讓這一行長了三分之一。

              更不能寫「還要瘦 X kg」：那等於目前體重減目標，就是被拿掉的
              「目標差距」，會直接洩漏體重。要看還差多少，本人那邊有側欄的
              體重 Boss HP。
            */}
            <b id="countdownCaption">
              {countdown.finished
                ? "比賽日到了"
                : `${baseline ? baseline.weight : "—"} → ${TARGET_END_WEIGHT} kg｜已執行 ${countdown.elapsed} 天`}
            </b>
            <span>跟著藍線，今天先跑完今天的。</span>
          </div>
          <div class="countdown-track">
            <div id="countdownProgress" style={{ width: `${countdown.progress.toFixed(1)}%` }} />
          </div>
        </div>
        <button id="openCalendarBtn" class="secondary compact-btn" onClick={actions.onOpenCalendar}>月曆</button>
      </article>

      {/*
        公里數是課表上的安排，不是實際跑量：App 沒有跑步距離的資料。
      */}
      <article id="weekPlan" class="week-strip brutal-card">
        <div>
          <span>今天的課表</span>
          <b id="weekToday">{workoutFor(planDate)[0]}</b>
        </div>
        <div>
          <span>本週課表</span>
          <b id="weekProgress">{week.done} / {week.total} 天</b>
        </div>
        <div>
          <span>{week.longRun.race ? "本週比賽" : "本週長跑"}（{formatDate(week.longRun.date)}）</span>
          <b id="weekLongRun">{week.longRun.km} km{week.longRun.done ? "・完成" : ""}</b>
        </div>
      </article>

      <div class="summary-grid summary-grid-primary" id="topSummary">
        <MetricCards cards={cards} openHelp={ui.openHelp} onToggleHelp={actions.onToggleHelp} />
      </div>

      <div class="today-layout">
        <article
          id="todayEditor"
          class={`today-editor brutal-card ${progress.complete ? "completed" : ""}`}
        >
          <DayEditor {...dayEditorPropsFor(props, planDate, "今日任務")} />
        </article>

        <aside class="today-support">
          <article id="npcCard" class={`mascot-card brutal-card npc-card npc-${npc.rank.grade.toLowerCase()}`}>
            <div class="npc-dialogue">
              <div class="npc-meta" aria-label="肉羹的狀態">
                <strong class="npc-name">🐱 肉羹</strong>
                <span id="npcRankTag" class="npc-status-line">🏅 {npc.rank.grade} Rank</span>
                <span id="npcStreakTag" class="npc-status-line">🔥 {npc.streak} 連勝</span>
              </div>
              <div id="npcSpeech" class="speech" aria-live="polite">{npc.text}</div>
            </div>
            <button
              id="npcMascotBtn" type="button"
              class={`npc-mascot-trigger ${ui.mascotBouncing ? "npc-bounce" : ""}`}
              aria-label="肉羹，按下可聽下一句對話"
              onClick={actions.onMascotClick}
              onAnimationEnd={actions.onMascotAnimationEnd}
            >
              <img src="/mascot.png" alt="肉羹貓貓在體重計上舉槓鈴" />
            </button>
          </article>

          <DayCheers names={cheeredToday} />

          <article class="daily-rules brutal-card">
            <h2>今日固定任務</h2>
            <div class="rule-chips">
              <span>三餐各有主蛋白</span><span>蔬菜至少 2 份</span><span>水 2.5L</span>
              <span>步數達 8,000</span>
              {/*
                保健品那格跟著例行面板一起只給本人——監督者已經看不到那一塊，
                留著這行規則只會讓他去找一個不存在的打勾。
              */}
              {can(role, "PUT", "/api/daily-logs") && <span>保健品只提醒、不計分</span>}
              <span>每日任務完成後獲得 Rank</span>
            </div>
          </article>
        </aside>
      </div>
    </TabPanel>
  );
}

function monthBounds(monthKey: string) {
  const [year, month] = monthKey.split("-").map(Number);
  return { year, month, lastDay: new Date(year, month, 0).getDate() };
}

function CalendarTab(props: AppProps) {
  const { data, today, ui, actions } = props;
  const { year, month, lastDay } = monthBounds(ui.calendarMonth);

  // 月曆從星期一開始，把 getDay()（週日為 0）位移成週一為 0
  const leading = (new Date(year, month - 1, 1, 12).getDay() + 6) % 7;
  const cells: (IsoDate | null)[] = Array(leading).fill(null);
  for (let day = 1; day <= lastDay; day++) {
    const date = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    cells.push(date < PLAN_START || date > PLAN_END ? null : date);
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const selectedProgress = dayCompletion(
    data.logs[ui.selectedDate] || {},
    data.healthMetrics[ui.selectedDate] || {}
  );

  return (
    <TabPanel name="calendar" active={ui.activeTab === "calendar"}>
      <div class="section-heading calendar-heading">
        <div>
          <p class="eyebrow">QUEST CALENDAR</p><h2>訓練月曆</h2>
          <span>月曆只負責瀏覽；預設仍回到今天編輯。</span>
        </div>
        <div class="calendar-controls">
          <button
            id="calendarPrevBtn" class="secondary" aria-label="上一個月"
            disabled={ui.calendarMonth <= PLAN_START.slice(0, 7)}
            onClick={() => actions.onCalendarShift(-1)}
          >←</button>
          <button id="calendarTodayBtn" onClick={actions.onCalendarToday}>今天</button>
          <button
            id="calendarNextBtn" class="secondary" aria-label="下一個月"
            disabled={ui.calendarMonth >= PLAN_END.slice(0, 7)}
            onClick={() => actions.onCalendarShift(1)}
          >→</button>
        </div>
      </div>

      <div class="calendar-layout">
        <article class="calendar-board brutal-card">
          <h2 id="calendarMonthTitle">{year} 年 {month} 月</h2>
          <div class="weekdays">
            <div>一</div><div>二</div><div>三</div><div>四</div><div>五</div><div>六</div><div>日</div>
          </div>
          <div id="calendarRoot" class="calendar-grid">
            <Calendar
              cells={cells}
              today={today}
              selectedDate={ui.selectedDate}
              logs={data.logs}
              healthMetrics={data.healthMetrics}
              view={data.measurements}
              cheers={data.cheers}
              onSelect={actions.onSelectDate}
            />
          </div>
        </article>
        <aside
          id="selectedDayEditor"
          class={`selected-day-editor brutal-card ${selectedProgress.complete ? "completed" : ""}`}
        >
          <DayEditor {...dayEditorPropsFor(props, ui.selectedDate, "選取日期")} />
          <DayCheers names={cheersOn(props, ui.selectedDate)} />
        </aside>
      </div>
    </TabPanel>
  );
}

function StatsTab({ data, today, planDate, profile, role, ui, actions }: AppProps) {
  const view = data.measurements;
  const baseline = seasonBaseline(data.season1Measurements);
  const cells = trendCells(view, planDate);
  const redacted = view.kind === "shapeOnly";

  /**
   * 遮蔽時**連算都不要算**。
   *
   * `energyGuideFor` 吃的是公斤，而遮蔽後的值是無單位的 0–1——`bmrEstimate`
   * 會照算出 `10 × 0.5 + …` 這種數字，不報錯，只是全錯。卡片不渲染還不夠，
   * 呼叫本身也要擋掉。`revealedRows()` 在遮蔽時回空陣列，所以就算這一行的
   * 判斷寫錯，算出來的也是一組空值而不是假數字。
   */
  const energy = redacted
    ? null
    : energyGuideFor({ measurements: revealedRows(view), today: planDate, date: planDate, profile, baseline });

  /*
   * 目標差距整張不給，不是改成箭頭。
   *
   * 它要跟 74.9kg 比，而那是個公斤錨點——前端一旦拿得到就破功（見
   * docs/adr/0004 的「只能有一個參考點」）。而且它的箭頭資訊量本來就低
   * （幾乎永遠是「還沒到」），翻負號的那一刻卻是全場最大的爆雷。
   *
   * 能量四卡同理不給：它們是本人調飲食的工具，監督者拿到也沒有用途。
   */
  const cards: MetricCardData[] = redacted
    ? [
      trendCard(cells.weight),
      trendCard(cells.weeklyChange),
      trendCard(cells.avgFat7),
      trendCard(cells.leanChange),
      trendCard(cells.prediction)
    ]
    : [
      trendCard(cells.weight),
      trendCard(cells.weeklyChange),
      trendCard(cells.avgFat7),
      trendCard(cells.leanChange),
      trendCard(cells.prediction),
      trendCard(cells.gapToTarget),
      ["估算 BMR", `${kcal(energy!.bmr)} kcal`, `${energy!.weightInfo.source}推算`, {
        title: "BMR｜基礎代謝率",
        body: "即使整天躺著不動，身體為了維持呼吸、心跳、體溫與器官運作，估計仍會消耗的熱量。",
        note: "依性別、年齡、身高與 7 日平均體重估算；不是實驗室實測，也不是建議你只吃這麼少。"
      }],
      ["今日估算 TDEE", `${kcal(energy!.tdee)} kcal`, `${energy!.config.label}；非實測值`, {
        title: "TDEE｜每日總消耗",
        body: "BMR 加上走路、日常活動、食物消化與今天課表活動後，估計用來維持目前體重的全天總消耗。",
        note: "會依今天是休息、重訓或跑步日而不同；運動消耗有誤差，不代表可以把估算熱量全部吃回去。"
      }],
      ["減脂參考攝取", `${kcal(energy!.low)}–${kcal(energy!.high)} kcal`, "放在統計參考，不需每日打卡"],
      ["建議平均赤字", `${kcal(energy!.plannedDeficit)} kcal／日`, "會隨 7 日平均體重小幅更新"]
    ];

  return (
    <TabPanel name="stats" active={ui.activeTab === "stats"}>
      <div class="section-heading">
        <div><p class="eyebrow">PLAYER STATS</p><h2>統計分析</h2></div>
        <span>用趨勢，不被單日數字嚇到</span>
      </div>
      <div id="statsCards" class="summary-grid stats-summary-grid">
        <MetricCards cards={cards} openHelp={ui.openHelp} onToggleHelp={actions.onToggleHelp} />
      </div>
      <BadgeWall
        list={badges({ ...data, measurements: view.rows }, view, today)}
        openId={ui.openAchievement}
        onToggle={actions.onToggleAchievement}
      />
      <div class="stats-chart-grid">
        <article class="chart-card brutal-card">
          <h2>體重趨勢</h2>
          <p>
            {redacted
              ? `每日體重與 7 日平均的起伏。數字在 ${RACE_DATE_LABEL} 賽事當天公開。`
              : `每日體重、7 日平均與 ${RACE_DATE_LABEL} 目標線。`}
          </p>
          <div id="weightChart" class="chart">
            {/*
              「遮蔽時不畫目標線」這條規則收在 chartSeries() 裡。targetWeight()
              是公開的線性公式，畫在跟體重線同一條 y 軸上——有它當尺，兩個點
              就能校準整條軸，拿不拿掉刻度標籤都一樣。這是「數字藏得住」的
              前提，見 docs/adr/0004，不能留給每個呼叫端記得。
            */}
            <LineChart {...chartSeries(view, "weight", undefined, baseline)} />
          </div>
        </article>
        <article class="chart-card brutal-card">
          <h2>體脂率趨勢</h2><p>以 7 日平均判斷，不因單日水分波動過度解讀。</p>
          <div id="fatChart" class="chart">
            <LineChart {...chartSeries(view, "fat")} />
          </div>
        </article>
        <article class="chart-card brutal-card">
          <h2>淨體重趨勢</h2><p>用 28 日平均輔助監控保肌；淨體重不等於骨骼肌量。</p>
          <div id="leanChart" class="chart">
            <LineChart {...chartSeries(view, "lean")} />
          </div>
        </article>
        <article class="chart-card brutal-card">
          <h2>每週達標率</h2>
          <p>訓練、三餐蛋白質、蔬菜、步數與喝水達標率；步數看數值，不是打勾。</p>
          <div id="complianceChart" class="chart">
            <ComplianceChart rows={weeklyCompliance({ ...data, today: planDate })} />
          </div>
        </article>
      </div>
      {/*
        匯出的是本人的完整資料（含備註），所以只有本人有。
        這一項不是端點權限——CSV 是前端就地組出來的，沒有 API 可以問，
        所以這裡只能直接看角色。
      */}
      {role === "owner" && (
        <div class="toolbar-row">
          <button id="downloadCsvBtn" class="secondary" onClick={actions.onDownloadCsv}>下載 CSV</button>
          <button id="printBtn" class="secondary" onClick={actions.onPrint}>列印／存成 PDF</button>
        </div>
      )}
    </TabPanel>
  );
}

/**
 * 戰報的另一個 adapter——數字全部來自 `battleReport()`，這裡只排版。
 * 另一個是 `domain/summary.ts` 的純文字版。要顯示哪幾項、用什麼標籤、排什麼
 * 順序兩邊各自決定，但**數字不可能不一樣**。
 */
function ShareTab({ data, today, planDate, profile, ui, actions }: AppProps) {
  // 分享頁整頁只有本人有（見 tabsFor），所以這裡永遠是 revealed
  const view = data.measurements;
  const baseline = seasonBaseline(data.season1Measurements);
  const { trend, countdown, energy, compliance: rates, assessment, rank, steps } =
    battleReport({ ...data, measurements: revealedRows(view), today: planDate, profile, baseline });

  // 數字與格式都來自 trendCells，這裡只挑哪幾格、什麼標籤、什麼順序
  const cells = trendCells(view, planDate);
  const text = (cell: TrendCell) => cell.text ?? "—";

  const metrics: [string, string][] = [
    ["賽事倒數", `${countdown.remaining} 天`],
    ["目前體重", text(cells.weight)],
    ["目標差距", text(cells.gapToTarget)],
    ["7日平均", text(cells.avg7)],
    ["本週變化", text(cells.weeklyChange)],
    [`${RACE_DATE_LABEL}預估`, text(cells.prediction)],
    ["今日步數", steps === null ? "—" : Math.round(steps).toLocaleString("zh-TW")],
    ["今日 Rank", rank.grade],
    ["估算 BMR", `${kcal(energy.bmr)} kcal`],
    ["減脂參考攝取", `${kcal(energy.low)}–${kcal(energy.high)} kcal`],
    ["7日平均體脂", text(cells.avgFat7)],
    ["28日淨體重變化", text(cells.leanChange)]
  ];

  return (
    <TabPanel name="share" active={ui.activeTab === "share"}>
      <div class="share-shell brutal-card">
        <div class="share-header">
          <div>
            <div class="eyebrow">FIGHT HEALTH</div>
            <h2>Angus 進度戰報</h2>
            <p id="shareDate">更新：{trend.latestDate || "尚無測量"}</p>
          </div>
          <span id="shareStatus" class={`status-pill ${assessment.cls}`}>{assessment.label}</span>
        </div>
        <div id="shareMetrics" class="share-metrics"><ShareMetrics metrics={metrics} /></div>
        <div class="share-chart-block">
          <h3>體重＋7日平均＋目標線</h3>
          <div id="shareWeightChart" class="chart compact">
            {/* 圖例比統計頁短——標籤是排版，各自決定；資料與 series 不是 */}
            <LineChart {...chartSeries(view, "weight", ["體重", "7日平均", "目標"], baseline)} />
          </div>
        </div>
        <div class="share-chart-block">
          <h3>體脂率趨勢</h3>
          <div id="shareBodyChart" class="chart compact">
            <LineChart {...chartSeries(view, "fat")} />
          </div>
        </div>
        <div class="share-chart-block">
          <h3>最近 14 日達標率</h3>
          <div id="shareCompliance">
            <ShareCompliance rows={[
              ["訓練", rates.workout], ["三餐蛋白質", rates.protein], ["蔬菜", rates.vegetables],
              ["步數", rates.steps], ["喝水", rates.water]
            ]} />
          </div>
        </div>
        <div id="shareAdvice" class="share-advice"><ShareAdvice assessment={assessment} /></div>
        <div class="share-chart-block">
          <h3>朋友怎麼猜</h3>
          <PredictionSummary
            predictions={data.racePredictions}
            loadFailed={ui.failedLoads.includes("racePredictions")}
            settlement={settleRace(data.racePredictions, revealedRows(view), data.raceResult, today)}
            reveal={revealState("share", ui, actions)}
          />
        </div>
        <div class="share-chart-block">
          <h3>誰在盯你</h3>
          <CheerBoard cheers={data.cheers} showDays />
        </div>
      </div>
      <div class="toolbar-row share-toolbar">
        <button id="copySummaryBtn" onClick={actions.onCopySummary}>複製文字戰報</button>
        <button id="sharePrintBtn" class="secondary" onClick={actions.onPrint}>列印／存成 PDF</button>
      </div>
      <p class="hint">在 iPhone 直接截圖這個分頁貼給 ChatGPT，即可進行進度解說與調整。</p>
    </TabPanel>
  );
}

function SettingsTab({ data, today, planDate, profile, ui, actions }: AppProps) {
  // 設定頁也只有本人有，所以這裡永遠是 revealed
  const energy = energyGuideFor({
    measurements: revealedRows(data.measurements), today, date: planDate, profile,
    baseline: seasonBaseline(data.season1Measurements)
  });

  return (
    <TabPanel name="settings" active={ui.activeTab === "settings"}>
      <div class="section-heading"><div><p class="eyebrow">GAME SETTINGS</p><h2>設定</h2></div></div>
      <div class="settings-grid">
        <article class="settings-card brutal-card energy-settings-card">
          <h2>代謝估算設定</h2>
          <p>只需確認一次。BMR 使用 Mifflin–St Jeor 公式；TDEE 依當日課表估算，並以 7 日平均體重優先計算。</p>
          <form
            id="energySettingsForm" class="energy-settings-form"
            onSubmit={event => {
              event.preventDefault();
              const form = event.currentTarget as HTMLFormElement;
              actions.onSaveProfile({
                ...profile,
                sex: (form.querySelector<HTMLSelectElement>("#energySex")?.value === "female" ? "female" : "male"),
                age: Number(form.querySelector<HTMLInputElement>("#energyAge")?.value),
                heightCm: Number(form.querySelector<HTMLInputElement>("#energyHeight")?.value)
              });
            }}
          >
            <label>生理性別
              <select id="energySex" value={profile.sex}>
                <option value="male">男</option><option value="female">女</option>
              </select>
            </label>
            <label>年齡<input id="energyAge" type="number" min="18" max="90" inputmode="numeric" value={profile.age} /></label>
            <label>身高（cm）<input id="energyHeight" type="number" min="130" max="220" step="0.1" inputmode="decimal" value={profile.heightCm} /></label>
            <button type="submit">儲存並重新計算</button>
          </form>
          <div id="energySettingsPreview" class="settings-preview">
            <EnergyPreview
              source={energy.weightInfo.source}
              weight={fmt(energy.weightInfo.weight)}
              bmr={kcal(energy.bmr)}
              tdee={kcal(energy.tdee)}
            />
          </div>
          <small>這三項設定儲存在目前瀏覽器；體重會由 InBody 同步後自動更新。估算值不是醫療檢測，也不代表實際吃進或實際消耗的熱量。</small>
        </article>

        <article class="settings-card brutal-card">
          <h2>同步狀態</h2>
          <dl id="syncStatus" class="details-list">
            <SyncStatus status={ui.syncStatus} error={ui.syncError} />
          </dl>
          <button id="testRefreshBtn" class="secondary" onClick={actions.onRecheckSync}>重新檢查</button>
        </article>

        <RaceResultCard result={data.raceResult} today={today} ui={ui} actions={actions} />

        <article id="season1Card" class="settings-card brutal-card">
          <h2>1.0 生日賽季</h2>
          <p>看當時的完整介面，成績與競猜結果都在裡面，只能看不能改。</p>
          <div class="season-actions">
            <a id="settingsReviewLink" class="season-link" href="/1.0/">回顧 1.0</a>
          </div>
        </article>

        <article class="settings-card brutal-card">
          <h2>資料來源</h2>
          <p>資料有兩條路進來：iPhone 捷徑把 InBody 與步數同步到 Cloudflare D1，或是你自己在月曆上補登。捷徑壞掉時補登就是唯一的路——在月曆點那一天，體重與步數都填得進去。睡眠不蒐集、不儲存。</p>
          <code id="syncEndpoint">{ui.syncEndpoint}</code>
        </article>

        <article class="settings-card brutal-card">
          <h2>安全性</h2>
          <p>儀表板以 HttpOnly Cookie 保護；捷徑以獨立 Bearer Token 同步。Token 不要放進公開程式碼或截圖。</p>
          <button id="logoutBtn" class="danger" onClick={actions.onLogout}>登出</button>
        </article>

        {ui.badge.supported && (
          <article class="settings-card brutal-card">
            <h2>任務角標</h2>
            <p>在主畫面的 App 圖示上顯示今天還沒完成的核心任務數，勾掉一項就少一個，全部完成就消失。</p>
            <label class="core-task" id="badgeToggle">
              <input
                type="checkbox"
                checked={ui.badge.enabled}
                onChange={event => actions.onToggleBadge((event.currentTarget as HTMLInputElement).checked)}
              />
              <span>
                <b>顯示任務角標</b>
                <small>
                  {ui.badge.blocked
                    ? "iPhone 需要通知權限才畫得出角標，目前是拒絕狀態，請到 iOS 設定裡改回允許。"
                    : "第一次開啟會詢問通知權限——iPhone 沒有它就畫不出角標。"}
                </small>
              </span>
            </label>
            <small>角標只在 App 開著時更新。跨過午夜之後它會停在昨天的數字，直到你再打開一次。</small>
          </article>
        )}

        <article class="settings-card brutal-card">
          <h2>安裝到 iPhone</h2>
          <p>使用 Safari 開啟網站，點「分享」→「加入主畫面」，即可像 App 一樣全螢幕使用。</p>
        </article>
      </div>
      <p id="buildInfo" class="build-info">{ui.buildInfo}</p>
    </TabPanel>
  );
}

/**
 * 誰來看得最勤。本人在分享頁看、監督者在戳戳樂頁看——兩種角色永遠不會同時
 * 渲染（本人沒有戳戳樂頁、監督者沒有分享頁），所以 id 不會重複。
 */
function CheerBoard({ cheers, showDays }: { cheers: CheerRow[]; showDays: boolean }) {
  /*
   * 監督者只拿得到名字，而且是照名字排的。
   * 「誰戳了幾天」只有本人該知道——理由與排序的道理見 domain/cheers.ts。
   */
  const rows = showDays
    ? cheerLeaderboard(cheers).map(row => [row.nickname, `來看過 ${row.days} 天`] as const)
    : cheerParticipants(cheers).map(nickname => [nickname, ""] as const);

  return (
    <div id="cheerBoard" class="bet-board">
      {rows.length === 0 && <small>還沒有人來看過。</small>}
      {rows.map(([nickname, detail]) => (
        <div key={nickname} class="bet-row reach">
          <b>{nickname}</b>
          <span>{detail}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * 結算與排名。**分享頁與競猜頁共用**——下注的是監督者，開獎當天看不到結果
 * 的話這個玩法就少了結尾。
 *
 * `rankingId` 由呼叫端給而不是寫死：所有分頁都常駐在 DOM，同一個 id 出現
 * 兩次就是重複 id（`DayEditor` 的註解記著同一個教訓）。class 兩邊一樣，
 * 樣式不受影響。
 *
 * 結算完全由日期與體重推導，**不需要按任何東西**；而 `settlePredictions`
 * 只在第一季結束後才給結果，所以不可能提前爆雷。
 */
function PredictionSettlement({ result, rankingId, reveal }: {
  /** null 代表資料還是遮蔽的，這時一律當成未結算。 */
  result: ReturnType<typeof settlePredictions> | null;
  rankingId: string;
  reveal: RevealState;
}) {
  if (!result || !result.settled) {
    return <p class="bet-camp-line">等 {SEASON_1.end} 生日當天結算。</p>;
  }
  return (
    <QuestionAwards
      id={rankingId} label="體重題" ranking={result.ranking} answerWasYes={!!result.reached}
      unit={formatKg} emptyText="這次沒有人猜對。" reveal={reveal} revealId="season-1-weight"
      fact={<>結算：{result.finalDate} 量到 {fmt(result.finalWeight)} kg，{result.reached ? "達標" : "沒到"}。</>}
    />
  );
}

/**
 * 結算、分隔行與完整名單。**分享頁與競猜頁共用同一份。**
 *
 * 共用的是**資料怎麼呈現**：排序（照 API 的 `ORDER BY nickname ASC`，不另外
 * 排——本人與監督者看到的順序一樣，對帳才對得起來）、陣營配色、沒填公斤數
 * 怎麼寫（`betLabel`）、結算後那條分隔行。這四件事只要有兩份就會漂移，而漂
 * 移的樣子是「監督者看到的注跟本人看到的不一樣」——**不會報錯**。
 *
 * 陣營那一行**不在裡面**：監督者看到的是「看好的 2 人」，本人看到的是「看好
 * **你**的 2 人」。人稱是措辭不是資料，與 `battleReport()` 的兩個 adapter 同一
 * 個分法——數字共用、措辭各自決定。
 *
 * 兩個 id 都由呼叫端給。兩種角色永遠不會同時渲染，但 id 仍然分開取——所有
 * 分頁都常駐在 DOM，這個習慣不留例外。
 */
function PredictionBoard({ predictions, settlement, rankingId, boardId, reveal, loadFailed }: {
  predictions: PredictionRow[];
  loadFailed: boolean;
  /** null 代表資料還是遮蔽的，這時一律當成未結算。 */
  settlement: ReturnType<typeof settlePredictions> | null;
  rankingId: string;
  boardId: string;
  reveal: RevealState;
}) {
  return (
    <>
      {predictions.length > 0 && (
        <PredictionSettlement result={settlement} rankingId={rankingId} reveal={reveal} />
      )}
      {/*
        開獎之後頒獎台與完整名單中間加一行，看得出哪裡是全部的人。名單的底色也
        從「押哪邊」換成「對或錯」，不然押對的人反而不像贏家。
      */}
      {settlement?.settled && predictions.length > 0 && (
        <p class="bet-camp-line">所有人的猜測</p>
      )}
      <div id={boardId} class="bet-board">
        {predictions.length === 0 && <small>{loadFailed ? "讀不到資料，請稍後再試。" : "還沒有人下注。"}</small>}
        {predictions.map(prediction => {
          const status = settlement?.settled
            ? betResult(settlement.ranking, true, prediction.nickname).status
            : "pending";
          return (
            <div key={prediction.nickname} class={`bet-row ${betTone([status]) ?? (prediction.will_reach ? "reach" : "miss")}`}>
              <b>{prediction.nickname}</b>
              <span>{betMark(status)}{betLabel(prediction)}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

/**
 * 兩題的結算與排名。**分享頁與競猜頁共用**——下注的是監督者，開獎當天看不到結果
 * 的話這個玩法就少了結尾。
 *
 * 結算完全由日期、體重與官方時間推導，**不需要按任何東西**。
 */
function RaceSettlementLines({ settlement, rankingId, reveal }: {
  settlement: RaceSettlement;
  rankingId: string;
  reveal: RevealState;
}) {
  const { weight, finish } = settlement;
  const doubles = doublePredictors(settlement);
  return (
    <>
      {doubles.length > 0 && (
        <DoubleRibbon id={`${rankingId}Double`} names={doubles} reveal={reveal} revealId="season-2-double" />
      )}
      {weight.settled ? (
        <QuestionAwards
          id={`${rankingId}Weight`} label="體重題" ranking={weight.ranking} answerWasYes={!!weight.reached}
          unit={formatKg} emptyText="這題沒有人猜對。" reveal={reveal} revealId="season-2-weight"
          fact={<>體重：{weight.finalDate} 量到 {fmt(weight.finalWeight)} kg，{weight.reached ? "達標" : "沒到"}。</>}
        />
      ) : (
        <p class="bet-camp-line">體重：等 {PLAN_END} 賽前晨量結算。</p>
      )}
      {finish.outcome === "pending" && <p class="bet-camp-line">完賽：等官方成績結算。</p>}
      {finish.outcome === "cancelled" && <p class="bet-camp-line">完賽：賽事取消，這一題作廢。</p>}
      {(finish.outcome === "finished" || finish.outcome === "not_finished") && (
        <QuestionAwards
          id={`${rankingId}Finish`} label="完賽題" ranking={finish.ranking} answerWasYes={finish.outcome === "finished"}
          unit={formatFinish} emptyText="這題沒有人猜對。" reveal={reveal} revealId="season-2-finish"
          fact={<>完賽：{finish.finishSeconds === null ? "未完賽" : formatFinishTime(finish.finishSeconds)}。</>}
        />
      )}
    </>
  );
}

/**
 * 結算與完整名單。**分享頁與競猜頁共用同一份**，排序照 API 的 `ORDER BY nickname
 * ASC`，兩種角色看到的順序一樣。人數那一行不在裡面：人稱是措辭不是資料。
 */
function RaceBoard({ predictions, settlement, rankingId, boardId, reveal, loadFailed }: {
  predictions: RacePredictionRow[];
  loadFailed: boolean;
  settlement: RaceSettlement;
  rankingId: string;
  boardId: string;
  reveal: RevealState;
}) {
  const hasResult = settlement.weight.settled || settlement.finish.outcome !== "pending";
  return (
    <>
      {predictions.length > 0 && <RaceSettlementLines settlement={settlement} rankingId={rankingId} reveal={reveal} />}
      {hasResult && predictions.length > 0 && <p class="bet-camp-line">所有人的猜測</p>}
      <div id={boardId} class="bet-board">
        {predictions.length === 0 && <small>{loadFailed ? "讀不到資料，請稍後再試。" : "還沒有人下注。"}</small>}
        {predictions.map(prediction => {
          const { weight, finish } = raceBetResults(settlement, prediction.nickname);
          return (
            <div key={prediction.nickname} class={`bet-row ${betTone([finish.status, weight.status]) ?? (prediction.will_finish ? "reach" : "miss")}`}>
              <b>{prediction.nickname}</b>
              <span class="bet-parts">
                <span class="bet-part">{betMark(finish.status)}{raceFinishLabel(prediction)}｜</span>
                <span class="bet-part">{betMark(weight.status)}{raceWeightLabel(prediction)}</span>
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

/**
 * 分享頁的競猜。本人看得到每個人怎麼猜——資料對兩種角色回同一份，只讓監督者
 * 看得到的話，被猜的那個人反而什麼都不知道。
 */
function PredictionSummary({ predictions, settlement, reveal, loadFailed }: {
  predictions: RacePredictionRow[];
  loadFailed: boolean;
  settlement: RaceSettlement;
  reveal: RevealState;
}) {
  const finishers = predictions.filter(bet => bet.will_finish).length;
  const reachers = predictions.filter(bet => bet.will_reach_weight).length;

  return (
    <div id="sharePredictions">
      {predictions.length > 0 && (
        <p class="bet-camp-line">
          看好你完賽 <b>{finishers}</b> 人，看好你達標 <b>{reachers}</b> 人，一共 <b>{predictions.length}</b> 人下注。
        </p>
      )}
      <RaceBoard
        predictions={predictions}
        loadFailed={loadFailed}
        settlement={settlement}
        rankingId="betRanking"
        boardId="shareBetBoard"
        reveal={reveal}
      />
    </div>
  );
}

/**
 * 官方完賽成績，本人在賽後補。
 *
 * 補的是時間，**不是「已完賽」這個結論**。平常整張卡不渲染：賽前打開設定頁看到
 * 一張填成績的卡片只會多一件要想的事。
 */
function RaceResultCard({ result, today, ui, actions }: {
  result: RaceResultRow | null;
  today: IsoDate;
  ui: AppUiState;
  actions: AppActions;
}) {
  const editable = canEditRaceResult(today);
  if (!editable && !result) return null;

  const outcome = finishOutcome(result);
  return (
    <article id="raceResultCard" class="settings-card brutal-card">
      <h2>半馬完賽成績</h2>
      <p id="raceResultNow">
        {outcome === "pending" && "還沒有補上成績。"}
        {outcome === "finished" && `官方完賽時間 ${formatFinishTime(result!.finish_seconds as number)}。`}
        {outcome === "not_finished" && "這次沒有完賽。"}
        {outcome === "cancelled" && "賽事取消。"}
      </p>
      {editable ? (
        <form
          id="raceResultForm" class="bet-form"
          onSubmit={event => {
            event.preventDefault();
            const form = event.currentTarget as HTMLFormElement;
            const picked = form.querySelector<HTMLInputElement>('input[name="raceOutcome"]:checked');
            actions.onSaveRaceResult({
              outcome: (picked?.value || "finished") as "finished" | "not_finished" | "cancelled",
              finish_time: readFinishTime(form, "raceFinishTime")
            });
          }}
        >
          <div class="bet-camps">
            <label class="core-task">
              <input id="raceFinished" type="radio" name="raceOutcome" value="finished" defaultChecked={outcome === "finished" || outcome === "pending"} />
              <span><b>完賽</b><small>填官方時間</small></span>
            </label>
            <label class="core-task">
              <input id="raceNotFinished" type="radio" name="raceOutcome" value="not_finished" defaultChecked={outcome === "not_finished"} />
              <span><b>未完賽</b><small>超過關門或沒起跑</small></span>
            </label>
            <label class="core-task">
              <input id="raceCancelled" type="radio" name="raceOutcome" value="cancelled" defaultChecked={outcome === "cancelled"} />
              <span><b>賽事取消</b><small>完賽這題作廢</small></span>
            </label>
          </div>
          <FinishTimeInput id="raceFinishTime" label="官方完賽時間（鳴槍起算）" value={result?.finish_seconds} />
          <RaceRules />
          <button type="submit">存起來</button>
          <div id="raceResultError" class="error-text">{ui.raceResultError}</div>
        </form>
      ) : (
        <p class="bet-closed">賽後 7 天已過，成績不能再改。</p>
      )}
    </article>
  );
}

/**
 * 誰來我家。
 *
 * 肉羹那句台詞最多念兩個名字（氣泡很窄，念完會把卡片撐爆），所以完整名單
 * 另外列一次。放在肉羹卡片下面而不是夾在任務清單中間——那裡會把「今天要做
 * 哪五件事」從中間切開。
 *
 * 沒有人來過就整塊不渲染，不要留一塊空的。
 */
function DayCheers({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  return (
    <article class="day-cheers brutal-card">
      <b>誰來我家</b>
      <div class="day-cheer-names">
        {names.map(name => <span key={name} class="cheer-name">{name}</span>)}
      </div>
    </article>
  );
}

/** 開獎後每一注前面的記號。不只靠底色分辨對錯。 */
function betMark(status: BetStatus): string {
  if (status === "correct") return "✓ ";
  return status === "wrong" ? "✗ " : "";
}

function revealState(tab: string, ui: AppUiState, actions: AppActions): RevealState {
  return { active: ui.activeTab === tab, seen: ui.seenReveals, onSeen: actions.onRevealSeen };
}

function betLabel(prediction: PredictionRow): string {
  const camp = prediction.will_reach ? "做得到" : "做不到";
  return prediction.weight_kg === null ? camp : `${camp}・${fmt(prediction.weight_kg)} kg`;
}

const RATE_LABELS = [
  ["workout", "課表"], ["protein", "三餐蛋白質"], ["vegetables", "蔬菜"], ["steps", "步數"], ["water", "喝水"]
] as const;

/**
 * 賽季成績單：生日賽季的最終結果。本人與監督者看到同一份，所以這裡完全不看角色。
 * 完整介面的入口只放設定頁，那裡每種版面都到得了。
 */
function SeasonReportTab({ data, today, ui, actions }: AppProps) {
  const noWeight = ui.failedLoads.includes("season1Measurements") ? "讀不到，請稍後再試" : "—";
  const report = seasonReport({
    measurements: data.season1Measurements,
    logs: data.season1Logs,
    healthMetrics: data.season1HealthMetrics,
    predictions: data.predictions,
    today
  });
  const rate = (value: number | null) => value === null ? "—" : `${Math.round(value)}%`;
  const myS1Bet = myPrediction(data.predictions, ui.nickname);

  return (
    <TabPanel name="report" active={ui.activeTab === "report"}>
      <div class="section-heading">
        <div><p class="eyebrow">SEASON 1.0</p><h2>1.0 成績單</h2></div>
        <button id="reportBackBtn" type="button" class="secondary compact-btn" onClick={() => actions.onTabChange("today")}>回到 2.0</button>
      </div>

      <div class="settings-grid">
        <article id="reportResult" class="settings-card brutal-card">
          <h2>生日賽季的結果</h2>
          <p>{SEASON_1.start} 到 {SEASON_1.end}，一共 {report.days} 天。目標是生日當天低於 {SEASON_1.targetWeight} kg。</p>
          <div class="bet-board">
            <div class="bet-row"><b>起始體重</b><span>{fmt(report.startWeight)} kg</span></div>
            <div class={`bet-row ${report.reached ? "reach" : "miss"}`}>
              <b>結算體重</b>
              <span>{report.finalWeight === null ? noWeight : `${fmt(report.finalWeight)} kg（${report.finalDate}）`}</span>
            </div>
            <div class={`bet-row ${report.reached ? "reach" : "miss"}`}>
              <b>結果</b>
              <span>{report.reached === null ? noWeight : report.reached ? "達標" : `沒到，一共減了 ${fmt(report.lost)} kg`}</span>
            </div>
          </div>
        </article>

        <article id="reportBets" class="settings-card brutal-card">
          <h2>1.0 的競猜結果</h2>
          {report.settlement.settled && myS1Bet && (
            <MyBetResult
              id="reportMyBetResult"
              rows={[{
                label: "體重題",
                result: betResult(report.settlement.ranking, true, myS1Bet.nickname),
                unit: formatKg
              }]}
            />
          )}
          <PredictionBoard
            predictions={data.predictions}
            loadFailed={ui.failedLoads.includes("predictions")}
            settlement={report.settlement}
            rankingId="reportBetRanking"
            boardId="reportBetBoard"
            reveal={revealState("report", ui, actions)}
          />
        </article>

        <article id="reportRates" class="settings-card brutal-card">
          <h2>任務達標率</h2>
          <p>整季 {report.days} 天裡，每一項達標的比例。</p>
          <div class="bet-board">
            {RATE_LABELS.map(([key, label]) => (
              <div key={key} class="bet-row"><b>{label}</b><span>{rate(report.rates[key])}</span></div>
            ))}
          </div>
        </article>

        <article id="reportRanks" class="settings-card brutal-card">
          <h2>Rank 分布</h2>
          <p>每一天拿到的評價。</p>
          <div class="bet-board">
            {(["S", "A", "B", "C", "D"] as const).map(grade => (
              <div key={grade} class="bet-row"><b>{grade} Rank</b><span>{report.rankCounts[grade]} 天</span></div>
            ))}
          </div>
        </article>
      </div>
    </TabPanel>
  );
}

const NO_GUESS = "—";
const BET_HOURS = [NO_GUESS, ...Array.from({ length: RACE_LIMIT_HOURS }, (_, index) => String(index + 1))];
const BET_MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));

function RaceRules() {
  return (
    <small class="bet-hint">
      半馬 {RACE_START_TIME} 起跑，限時 {RACE_LIMIT_HOURS} 小時。關門點：{RACE_CHECKPOINTS.map(point => `${point.km} km ${point.closes}`).join("、")}。
    </small>
  );
}

/** 猜完賽時間。小時停在「—」就是不猜，分鐘不看。 */
function BetTimeWheels({ value }: { value: number | null | undefined }) {
  const parts = finishTimeParts(value);
  return (
    <div class="bet-weight" role="group" aria-labelledby="betFinishTimeLabel">
      <span id="betFinishTimeLabel">猜完賽時間（選填，小時選 — 就是不猜）</span>
      <div class="finish-time wheel-picker">
        <Wheel
          id="betFinishHours" label="猜幾小時" unit="時" options={BET_HOURS}
          initial={Math.max(0, BET_HOURS.indexOf(parts.hours))}
        />
        <Wheel
          id="betFinishMinutes" label="猜幾分" unit="分" options={BET_MINUTES}
          initial={Math.max(0, BET_MINUTES.indexOf(parts.minutes))}
        />
      </div>
      <RaceRules />
    </div>
  );
}

function readBetTime(form: HTMLFormElement): string {
  const hours = wheelValue(form, "betFinishHours", BET_HOURS);
  return hours === NO_GUESS ? "" : joinFinishTime(hours, wheelValue(form, "betFinishMinutes", BET_MINUTES));
}

/**
 * 官方成績要精確到秒，滾輪不好選，所以用時、分、秒三格。手機的數字鍵盤沒有冒號，
 * 一格要打 `2:35:20` 的話打不出來。不受控，理由與其他競猜欄位相同。
 */
function FinishTimeInput({ id, label, value }: {
  id: string;
  label: string;
  value: number | null | undefined;
}) {
  const parts = finishTimeParts(value);
  const cell = (suffix: string, unit: string, length: number, hint: string, initial: string) => (
    <label>
      <input
        id={`${id}${suffix}`} type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="off"
        maxLength={length} placeholder={hint} defaultValue={initial}
      />
      <span>{unit}</span>
    </label>
  );
  return (
    <div class="bet-weight" role="group" aria-labelledby={`${id}Label`}>
      <span id={`${id}Label`}>{label}</span>
      <div class="finish-time">
        {cell("Hours", "時", 1, "2", parts.hours)}
        {cell("Minutes", "分", 2, "30", parts.minutes)}
        {cell("Seconds", "秒", 2, "00", parts.seconds)}
      </div>
    </div>
  );
}

function readFinishTime(form: HTMLFormElement, id: string): string {
  const read = (suffix: string) => form.querySelector<HTMLInputElement>(`#${id}${suffix}`)?.value ?? "";
  return joinFinishTime(read("Hours"), read("Minutes"), read("Seconds"));
}

function raceFinishLabel(bet: RacePredictionRow): string {
  const time = bet.finish_seconds === null ? "" : ` ${formatFinishTime(bet.finish_seconds)}`;
  return `${bet.will_finish ? "會完賽" : "不會完賽"}${time}`;
}

function raceWeightLabel(bet: RacePredictionRow): string {
  const kg = bet.weight_kg === null ? "" : ` ${fmt(bet.weight_kg)} kg`;
  return `${bet.will_reach_weight ? "會達標" : "不會達標"}${kg}`;
}

function raceBetLabel(bet: RacePredictionRow): string {
  return `${raceFinishLabel(bet)}｜${raceWeightLabel(bet)}`;
}

/**
 * 競猜：監督者對台北馬賽季結果的預測。
 *
 * 日期規則來自 shared/race-prediction.ts，與後端同一份。**這裡的封盤只是介面
 * 狀態**，真正擋住的是後端。
 */
function PredictionTab({ data, today, ui, actions }: AppProps) {
  const mine = myPrediction(data.racePredictions, ui.nickname);
  const open = canBetOnRace(today);
  const finishers = data.racePredictions.filter(bet => bet.will_finish).length;
  const reachers = data.racePredictions.filter(bet => bet.will_reach_weight).length;
  /*
   * 體重題只吃伺服器說已揭曉的公斤數。這裡的 `today` 來自裝置時鐘，遮蔽判斷來自
   * 伺服器時鐘；遮蔽時 revealedRows() 是空的，裝置調快也算不出「量到 0.5 kg」。
   */
  const settlement = settleRace(
    data.racePredictions, revealedRows(data.measurements), data.raceResult, today
  );
  const hasResult = settlement.weight.settled || settlement.finish.outcome !== "pending";
  const myResults = mine ? raceBetResults(settlement, mine.nickname) : null;

  return (
    <TabPanel name="prediction" active={ui.activeTab === "prediction"}>
      <div class="section-heading">
        <div><p class="eyebrow">RACE BET</p><h2>競猜</h2></div>
        <button id="openSeasonReport" type="button" class="secondary compact-btn" onClick={() => actions.onTabChange("report")}>1.0 成績單</button>
      </div>

      <div class="settings-grid">
        <article class="settings-card brutal-card">
          <h2>他做得到嗎？</h2>
          <p>
            {/*
              **中文文案不要跨行寫。** JSX 會把行尾的換行併成一個半形空格，
              於是畫面上會出現多出來的縫。
            */}
            猜兩件事：{PLAN_END} 臺北馬半程能不能完賽，賽前體重能不能到 {TARGET_END_WEIGHT} kg。想爭第一的話再填數字，結算時答對的人之間比誰最接近。
          </p>

          {mine && <p id="myBet" class="my-bet">你的猜測：<b>{raceBetLabel(mine)}</b></p>}

          {open ? (
            <>
              {/*
                表單刻意不受控（defaultValue／defaultChecked）。toast 消失、回到前景
                的自動重抓都會重繪，受控的話還沒送出的答案會被重置成舊值——而使用者
                只會看到自己選的東西變回去。
              */}
              <form
                id="betForm" class="bet-form"
                onSubmit={event => {
                  event.preventDefault();
                  const form = event.currentTarget as HTMLFormElement;
                  const value = (id: string) => form.querySelector<HTMLInputElement>(id);
                  const raw = value("#betWeight")?.value.trim();
                  actions.onBet({
                    will_finish: !!value("#betFinish")?.checked,
                    will_reach_weight: !!value("#betReach")?.checked,
                    weight_kg: raw ? Number(raw) : null,
                    finish_time: readBetTime(form)
                  });
                }}
              >
                <div class="bet-camps">
                  <label class="core-task">
                    <input
                      id="betFinish" type="radio" name="willFinish"
                      defaultChecked={mine ? !!mine.will_finish : true}
                    />
                    <span><b>會完賽</b><small>有官方完賽成績</small></span>
                  </label>
                  <label class="core-task">
                    <input
                      id="betNoFinish" type="radio" name="willFinish"
                      defaultChecked={mine ? !mine.will_finish : false}
                    />
                    <span><b>不會完賽</b><small>超過關門或沒起跑</small></span>
                  </label>
                </div>

                <BetTimeWheels value={mine?.finish_seconds} />

                <div class="bet-camps">
                  <label class="core-task">
                    <input
                      id="betReach" type="radio" name="willReachWeight"
                      defaultChecked={mine ? !!mine.will_reach_weight : true}
                    />
                    <span><b>會達標</b><small>賽前不超過 {TARGET_END_WEIGHT} kg</small></span>
                  </label>
                  <label class="core-task">
                    <input
                      id="betMiss" type="radio" name="willReachWeight"
                      defaultChecked={mine ? !mine.will_reach_weight : false}
                    />
                    <span><b>不會達標</b><small>差一點也算沒到</small></span>
                  </label>
                </div>

                <label class="bet-weight" for="betWeight">
                  <span>猜幾公斤（選填）</span>
                  <input
                    id="betWeight" type="number" step="0.1" min="25" max="300" placeholder="75.0"
                    defaultValue={mine?.weight_kg === null || mine?.weight_kg === undefined ? "" : String(mine.weight_kg)}
                  />
                </label>

                <button type="submit" disabled={!ui.nickname}>
                  {mine ? "改成這樣" : "送出猜測"}
                </button>
              </form>

              {!ui.nickname && (
                <small class="bet-hint">先到「身分」分頁填一個名字，才知道這一注是誰下的。</small>
              )}
              <div id="predictionError" class="error-text">{ui.predictionError}</div>
            </>
          ) : mine && hasResult ? (
            <MyBetResult
              id="myBetResult"
              double={myResults!.weight.status === "correct" && myResults!.finish.status === "correct"}
              rows={[
                { label: "體重題", result: myResults!.weight, unit: formatKg },
                { label: "完賽題", result: myResults!.finish, unit: formatFinish }
              ]}
            />
          ) : (
            <p class="bet-closed">{PLAN_END} 當天封盤，已經不收新的猜測了。</p>
          )}
        </article>

        <article class="settings-card brutal-card">
          <h2>大家怎麼猜</h2>
          <p>看好完賽的 {finishers} 人，看好達標的 {reachers} 人，一共 {data.racePredictions.length} 人。</p>
          {/*
            下注的是監督者，所以開獎當天結果要出現在**他們的**畫面上。
          */}
          <RaceBoard
            predictions={data.racePredictions}
            loadFailed={ui.failedLoads.includes("racePredictions")}
            settlement={settlement}
            rankingId="viewerRaceRanking"
            boardId="raceBoard"
            reveal={revealState("prediction", ui, actions)}
          />
        </article>

      </div>
    </TabPanel>
  );
}

/**
 * 監督者的第五格：戳戳樂。
 *
 * 先前這一頁只放暱稱與登出，填完名字之後就再也沒有用了；而戳的按鈕夾在
 * Rank 與任務清單中間，要滾一段才看得到。合成同一頁之後，**戳直接進到
 * 分頁列**，而暱稱退成它本來的角色——戳之前要先有名字。
 *
 * 每一天的戳仍然留在該日的編輯器裡（月曆選過去的某一天就是從那裡戳），
 * 這一頁負責的是「戳今天」與「誰來得最勤」。
 */
function CheerTab({ data, planDate, ui, actions }: AppProps) {
  // 與日編輯器那一格是同一支，所以兩處不可能給出不同答案
  const cheeredToday = cheeredBy(data.cheers, planDate, ui.nickname);

  return (
    <TabPanel name="cheer" active={ui.activeTab === "cheer"}>
      <div class="section-heading"><div><p class="eyebrow">SUPPORTER</p><h2>戳戳樂</h2></div></div>

      {/*
        **兩種狀態不是同一張卡換文字，是兩種份量。**

        還沒戳的時候這是整頁唯一要你做的事，值得大貓、大按鈕、完整說明；
        戳完之後它只是一張回條——「今天做完了、明天再來」——說明只有第一次
        有用。先前兩種狀態共用同一張卡，於是一張回條拿到了頭條的份量，在
        手機上佔掉三分之一個螢幕，還跟品牌列搶份量。

        收成回條之後，這一頁的主角自然變成「還有誰在盯他」，那才是戳完之後
        真正想看的東西。回條用 --lime 是沿用既有語彙：這個 app 裡萊姆綠一直
        代表「完成」（選中的分頁、打勾的任務）。
      */}
      {cheeredToday ? (
        <article
          id="cheerDone"
          class={`cheer-done brutal-card ${ui.cheerBouncing ? "is-morphing" : ""}`}
          onAnimationEnd={actions.onCheerAnimationEnd}
        >
          {/*
            戳完之後肉羹會變身：跳到最高點那一格，舉重版換成應援版。兩張圖
            都在 DOM 裡（另一張 opacity:0），所以換的那一瞬間不需要等載入。
          */}
          <span class="cheer-mascot">
            <img src="/mascot.png" alt="" aria-hidden="true" />
            <img src="/mascot-supporter-mini.png" alt="" aria-hidden="true" />
          </span>
          <div>
            <b>今天戳過了 ✓</b>
            <small>他打開 App 就會看到你的名字。明天再來。</small>
          </div>
        </article>
      ) : (
        <article class="cheer-hero brutal-card">
          <span class="cheer-mascot">
            <img src="/mascot.png" alt="" aria-hidden="true" />
            <img src="/mascot-supporter-mini.png" alt="" aria-hidden="true" />
          </span>
          <div>
            <h2>讓他知道你在看</h2>
            <p>
              {ui.nickname
                ? (
                  <>
                    {/*
                      **「肉羹」不能被拆到兩行。** 中文預設哪裡都能斷行，實機上
                      就出現過「他打開 App 時肉／羹會講出你的名字」——吉祥物的
                      名字斷開，讀起來像錯字。

                      **最後那個短句也整句不拆。** 追著字數調沒有用——任何固定
                      字串都會在某個寬度孤行（402px 上就出現過「一天一次就／
                      好。」）。讓它整句換行，第二行才是完整的一句話。

                      日期拿掉了：它是這段裡最長的東西，320px 上會把說明推成
                      三行，招呼卡就跟回條等不了高。標題與回條都已經說了「今天」，
                      而戳的對象本來就只有今天。
                    */}
                    他打開 App 時<span class="keep-together">肉羹</span>會講出你的名字。
                    <span class="keep-together">一天一次就好。</span>
                  </>
                )
                : "先在下面填一個名字，他才知道是誰戳的。"}
            </p>
            <button
              id="cheerTodayBtn" type="button"
              disabled={!ui.nickname}
              onClick={() => actions.onCheer(planDate)}
            >
              戳一下
            </button>
          </div>
        </article>
      )}

      <div class="settings-grid">
        <article class="settings-card brutal-card">
          <h2>還有誰在盯他</h2>
          <p>誰來了幾天只有他自己看得到。</p>
          <CheerBoard cheers={data.cheers} showDays={false} />
        </article>

        <article class="settings-card brutal-card">
          <h2>你的名字</h2>
          <p>
            戳他的時候會帶上這個名字。只收中英文與數字，長度上限是 {NICKNAME_MAX_WIDTH / 2} 個中文字或 {NICKNAME_MAX_WIDTH} 個英文字母。
          </p>
          <form
            id="nicknameForm" class="nickname-form"
            onSubmit={event => {
              event.preventDefault();
              const input = (event.currentTarget as HTMLFormElement)
                .querySelector<HTMLInputElement>("#nicknameInput");
              if (input) actions.onSaveNickname(input.value);
            }}
          >
            {/*
              **`defaultValue` 而不是 `value`。**

              受控的話任何一次重繪都會把使用者正在打的字覆蓋回已存的值，而
              重繪隨時都在發生：toast 兩秒後自動關閉、回到前景重抓資料、
              戳一下⋯⋯。更糟的是填了不合規的名字時，`setUi({ nicknameError })`
              本身就會觸發重繪——畫面告訴你「只能用中英文與數字」，同時把你
              打的字清空，連錯在哪都看不到。

              `key` 綁在已存的名字上：只有**存檔成功**時它才會變，這時才重新
              掛載輸入框、把正規化後的值放進去。打字期間狀態不動，所以不會
              重新掛載，文字就留得住。
            */}
            <input
              key={ui.nickname}
              id="nicknameInput" type="text" placeholder="阿明"
              autocomplete="nickname" defaultValue={ui.nickname}
            />
            <button type="submit">存起來</button>
          </form>
          <div id="nicknameError" class="error-text">{ui.nicknameError}</div>
          <p class="cheer-note">
            你看到的任務、Rank、體重與步數都是真實紀錄，但這裡的任何一格你都改不了；他每天寫的備註只有他自己看得到。
          </p>
          <button id="identityLogout" class="danger" onClick={actions.onLogout}>登出</button>
        </article>
      </div>
    </TabPanel>
  );
}

/**
 * 畫面最上面的橫幅。
 *
 * 有新版的時候蓋過公告：那一條點下去就解決，公告則是「你之後要做某件事」，
 * 兩條同時出現只會讓人兩條都不讀。
 */
function Banner({ role, ui, actions }: AppProps) {
  if (ui.updateAvailable) {
    return (
      <div id="updateBanner" class="app-banner update">
        <span>有新版了，重新載入就會換過去。</span>
        <button id="updateReloadBtn" type="button" onClick={actions.onReload}>重新載入</button>
      </div>
    );
  }
  if (!ui.notice) return null;
  return (
    <div id="noticeBanner" class={`app-banner ${ui.notice.links?.length ? "has-links" : ""}`}>
      <span>{ui.notice.text}</span>
      {ui.notice.links?.includes("season-report") && (
        <button id="noticeReportBtn" type="button" class="secondary" onClick={() => actions.onTabChange("report")}>1.0 成績單</button>
      )}
      {ui.notice.links?.includes("prediction") && can(role, "POST", "/api/race-predictions") && (
        <button id="noticePredictionBtn" type="button" class="secondary" onClick={() => actions.onTabChange("prediction")}>去競猜</button>
      )}
      <button
        id="noticeDismissBtn" type="button" class="secondary"
        onClick={() => actions.onDismissNotice(ui.notice!.id)}
      >知道了</button>
    </div>
  );
}

/**
 * 給監督者的「加入主畫面」提示。該不該出現與內容都已經算好了，這裡只畫。
 *
 * LINE 的連結不用另開分頁：LINE 看到參數會自己交給外部瀏覽器。
 */
function InstallHintCard({ ui, actions }: { ui: AppUiState; actions: AppActions }) {
  const hint = ui.installHint;
  if (!hint) return null;
  return (
    <aside id="installHint" class={`install-hint ${hint.platform}`} aria-label="加入主畫面">
      <p>
        {hint.before}
        {hint.platform === "line"
          ? <a id="installHintLink" href={ui.externalBrowserHref}>{hint.action}</a>
          : <b>{hint.action}</b>}
        {hint.after}
      </p>
      <button id="installHintDismiss" type="button" class="secondary compact-btn" onClick={actions.onDismissInstallHint}>知道了</button>
    </aside>
  );
}

export function App(props: AppProps) {
  const { role, ui, actions } = props;
  // 分頁清單是唯一的來源——有那一格才渲染那一頁，不要另外判斷角色
  const visible = new Set(tabsFor(role).map(([tab]) => tab));

  return (
    <>
      <LoginOverlay visible={ui.loginVisible} error={ui.loginError} onLogin={actions.onLogin} />

      <div class="app-shell">
        <Banner {...props} />
        <Sidebar {...props} />
        <div class="content-shell">
          <AppHeader {...props} />
          <main>
            <TodayTab {...props} />
            <CalendarTab {...props} />
            <StatsTab {...props} />
            {visible.has("share") && <ShareTab {...props} />}
            {visible.has("settings") && <SettingsTab {...props} />}
            {visible.has("prediction") && <PredictionTab {...props} />}
            {visible.has("cheer") && <CheerTab {...props} />}
            <SeasonReportTab {...props} />
          </main>
        </div>
      </div>

      <InstallHintCard ui={ui} actions={actions} />
      <div id="toast" class={`toast ${ui.toastVisible ? "" : "hidden"}`}>{ui.toast}</div>
    </>
  );
}
